import React, { useState } from 'react';
import './LoginSignUp.css';
import { Spinner, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import GoogleButton from 'react-google-button';
import { useUserAuth } from '../Context/UserAuthContext';
import logo from '../images/lawbot.jpg';

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { logIn, googleSignIn } = useUserAuth();
  const navigate = useNavigate();

  const getErrorMessage = (error) => {
    const message = error.message || error;
    if (message.includes('user-not-found')) return 'No account found with this email. Please sign up first.';
    if (message.includes('wrong-password')) return 'Incorrect password. Please try again.';
    if (message.includes('invalid-email')) return 'Please enter a valid email address.';
    if (message.includes('invalid-credential')) return 'Invalid email or password. Please check your credentials.';
    if (message.includes('too-many-requests')) return 'Too many failed attempts. Please try again later.';
    if (message.includes('network')) return 'Network error. Please check your internet connection.';
    return 'Login failed. Please try again.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    if (!email.includes('@')) {
      setError("Please enter a valid email address");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);
    try {
      await logIn(email, password);
      navigate("/home");
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError("");
    setLoading(true);
    try {
      await googleSignIn();
      navigate("/home");
    } catch (err) {
      if (err.message.includes('popup-closed')) {
        setError("Sign-in popup was closed. Please try again.");
      } else if (err.message.includes('unauthorized-domain')) {
        setError("This domain is not authorized. Please contact support.");
      } else {
        setError(getErrorMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Left Side - Image */}
      <div className="auth-image-side">
        <div className="auth-image-content">
          <h2>Welcome Back to LawBot</h2>
          <p>
            Access your legal queries and get AI-powered consultation based on 
            Pakistan Penal Code and Constitution. Sign in to continue your legal journey.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="auth-form-side">
        <div className="auth-container">
          <div className="auth-card">
            {/* Logo Section */}
            <div className="auth-logo">
              <img src={logo} alt="LawBot Logo" />
              <h1 className="auth-title">Sign In</h1>
              <p className="auth-subtitle">AI-Powered Legal Intelligence</p>
            </div>

            {/* Error Display */}
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            {/* Login Form */}
            <Form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label className="form-label" htmlFor="email">
                  Email Address
                </label>
                <input
                  id="email"
                  className="form-control"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  className="form-control"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    Signing In...
                  </>
                ) : (
                  'Sign In'
                )}
              </button>
            </Form>

            {/* Divider */}
            <div className="auth-divider">
              <span>OR</span>
            </div>

            {/* Google Sign In */}
            <div className="google-btn-wrapper">
              <GoogleButton
                type="dark"
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{ width: '100%' }}
              />
            </div>

            {/* Footer */}
            <div className="auth-footer">
              <p>
                Don't have an account?{' '}
                <Link to="/signup">Sign up now</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
